import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { AfterViewInit, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute, ActivatedRouteSnapshot } from "@angular/router";
import { IonContent, IonModal, ModalController, Platform } from "@ionic/angular";
import { OidcSecurityService } from "angular-auth-oidc-client";
import { catchError, firstValueFrom, forkJoin, Observable, of, Subscription } from "rxjs";
import * as xmljs from "xml-js";
import { environment } from "../../environments/environment";
import { FairAppOdataAktRequest } from "../api/models/fair-app-odata-akt-request";
import { Gutachten } from "../api/models/gutachten";
import { GutachtenRequest } from "../api/models/gutachten-request";
import { KalkulationGutachtenPosition } from "../api/models/kalkulation-gutachten-position";
import { KalkulationVorlage } from "../api/models/kalkulation-vorlage";
import { OdataGutachten } from "../api/models/odata-gutachten";
import { OdataGutachtenPosition } from "../api/models/odata-gutachten-position";
import { OdataGutachtenPositionBatchPost } from "../api/models/odata-gutachten-position-batch-post";
import { GutachtenService } from "../api/services/gutachten.service";
import { TitelService } from "../api/services/titel.service";
import { ComponentCanDeactivate } from "../general/component-can-deactivate";
import { KalkulationFavoritenListComponent } from "../util/components/kalkulation-favoriten-list/kalkulation-favoriten-list.component";
import { KalkulationVorlagenListComponent } from "../util/components/kalkulation-vorlagen-list/kalkulation-vorlagen-list.component";
import { FileViewerComponent } from "../util/components/pdf-viewer/file-viewer.component";
import {
  GutachtenAbloesevorschlag,
  GutachtenKalkulationAbrechnungsArt,
  GutachtenPositionsArt,
  KalkulationsArt,
  UserRole,
} from "../util/enums/global-enums";
import { deepCopyArray, deepCopyFormArray, isMobile, roundToDecimals } from "../util/global-functions/global-functions";
import { AuthService } from "../util/services/auth.service";
import { LoveItAlertService } from "../util/services/love-it-alert.service";
import { LoveItToastService } from "../util/services/love-it-toast.service";

enum NgSelectNameEnum {
  Positionsgrupper = "Positionsgrupper",
  Bezeichnung = "Bezeichnung",
  Einheit = "Einheit",
}

export enum odataGutachtenPositionPropertyLabels {
  Abloese = "Ablöse",
  Abzug_Vorschaeden = "%\u00A0NW",
  Angebotspreis = "Positionspreis / Neuwert",
  AngebotspreisZeit = "Zeitwert",
  Art = "Art",
  Bemerkung = "Position",
  Bezeichnung = "Position",
  Eigenleistung = "Eigen\u00ADleistung",
  Einheit = "EH",
  Einheitspreis = "Einheitspreis",
  EinheitspreisZeit = "Einheitspreis Zeit",
  Gruppe = "Gruppe",
  GutachtenId = "Gutachten-ID",
  KalkulationsArt = "Kalkulationsart",
  Menge = "Menge",
  Oid = "Oid",
  Positionsgrupper = "Positionsgruppe",
  Pruef_OK = "Prüfung-OK",
  Sort = "Sort",
  UStVorhanden = "MwSt.",
}

@Component({
  selector: "app-kalkulation",
  templateUrl: "./kalkulation.page.html",
  styleUrls: ["./kalkulation.page.scss"],
  standalone: false,
})
export class KalkulationPage extends ComponentCanDeactivate implements OnInit, AfterViewInit, OnDestroy {
  hasUnsavedChanges(): boolean {
    return this.gutachtenFormGroup.dirty || this.positionenFormGroup.dirty;
  }

  submitted: boolean = false;
  loadingPdf: boolean = false;
  loadingPositionen: boolean = false;
  loadingGutachten: boolean = false;
  loadedGutachten: OdataGutachten;
  deactivatePositionen: boolean = true;
  gutachtenId: number;
  droppedRowOid: number | null = null;

  private _readonlyConditionMet: boolean = false;
  get readonlyConditionMet() {
    return this._readonlyConditionMet;
  }
  set readonlyConditionMet(readonly: boolean) {
    this._readonlyConditionMet = readonly;
    if (readonly) {
      this.gutachtenFormGroup.disable({ emitEvent: false });
      this.positionenFormGroup.disable({ emitEvent: false });
    } else if (!this.deactivatePositionen) {
      this.gutachtenFormGroup.enable({ emitEvent: false });
      this.positionenFormGroup.enable({ emitEvent: false });
    }
  }

  ngSelectValues = new Array<any>();

  unsavedPositionenChanges: boolean = false;
  unsavedGutachtenChanges: boolean = false;

  @ViewChild("kalkulationsArtModal") kalkulationsArtModal: IonModal;
  currentKalkulationsArtModalFormControl: FormControl;

  @ViewChild("kalkulationPositionskategorieModal")
  kalkulationPositionskategorieModal: IonModal;
  kalkulationPositionskategorieModalKategorieFormControl: FormControl;
  kalkulationsArtValueLabelOptions: { value: string; label: string }[] = [];

  @ViewChild(IonContent) private ionContent: IonContent;
  @ViewChild(MatSort) sort: MatSort;
  displayedColumns: string[] = [
    "Sort",
    "Positionsgrupper",
    "Bezeichnung",
    "Menge",
    "Einheit",
    "Einheitspreis",
    "Angebotspreis",
    "Abzug_Vorschaeden",
    "AngebotspreisZeit",
    "UStVorhanden",
    "Abloese",
    "Eigenleistung",
    "actions",
  ];
  dataSourceMatTable: MatTableDataSource<any>;
  positionen: KalkulationGutachtenPosition[] = [];
  currentlyEditedPosition: OdataGutachtenPosition;
  aktId: number;
  titel: string;
  shortSB2: string;

  selector: FormControl<KalkulationsArt> = new FormControl(KalkulationsArt.Gebaeude);
  selectorOptions = KalkulationsArt;

  abrechnungsartOptions = Object.keys(GutachtenKalkulationAbrechnungsArt).map((key) => ({
    value: key,
    label: GutachtenKalkulationAbrechnungsArt[key],
  }));

  abloesevorschlagOptions = Object.keys(GutachtenAbloesevorschlag).map((key) => ({
    value: key,
    label: GutachtenAbloesevorschlag[key],
  }));

  positionsgrupperOptions = [
    { value: "Baumeisterarbeiten", label: "Baumeisterarbeiten" },
    { value: "Bodenlegerarbeiten", label: "Bodenlegerarbeiten" },
    { value: "Brandschadenarbeiten", label: "Brandschadenarbeiten" },
    { value: "Dachdeckerarbeiten", label: "Dachdeckerarbeiten" },
    { value: "Eigenleistung", label: "Eigenleistung" },
    { value: "Elektrikerarbeiten", label: "Elektrikerarbeiten" },
    { value: "Fassadenarbeiten", label: "Fassadenarbeiten" },
    {
      value: "Fenster-/Türen-/Sonnenschutzarbeiten",
      label: "Fenster-/Türen-/Sonnenschutzarbeiten",
    },
    { value: "Fliesenlegerarbeiten", label: "Fliesenlegerarbeiten" },
    { value: "Gartenbauarbeiten", label: "Gartenbauarbeiten" },
    { value: "Glaserarbeiten", label: "Glaserarbeiten" },
    { value: "Kanalsanierungsarbeiten", label: "Kanalsanierungsarbeiten" },
    { value: "Installateurarbeiten", label: "Installateurarbeiten" },
    { value: "Leckortung", label: "Leckortung" },
    { value: "Maler-/Tapeziererarbeiten", label: "Maler-/Tapeziererarbeiten" },
    { value: "Sanierungsarbeiten", label: "Sanierungsarbeiten" },
    { value: "Schlosserarbeiten", label: "Schlosserarbeiten" },
    { value: "Schwarzdeckerarbeiten", label: "Schwarzdeckerarbeiten" },
    { value: "Sonstiges", label: "Sonstiges" },
    { value: "Spenglerarbeiten", label: "Spenglerarbeiten" },
    { value: "Stehlgut", label: "Stehlgut" },
    { value: "Stromkosten", label: "Stromkosten" },
    { value: "Tiefbauarbeiten", label: "Tiefbauarbeiten" },
    { value: "Tischlerarbeiten", label: "Tischlerarbeiten" },
    { value: "Trockenbauarbeiten", label: "Trockenbauarbeiten" },
    { value: "Trocknungsarbeiten", label: "Trocknungsarbeiten" },
    { value: "Zaunbauarbeiten", label: "Zaunbauarbeiten" },
    { value: "Zimmererarbeiten", label: "Zimmererarbeiten" },
  ];

  bezeichnugOptions = [
    { value: "Abbrucharbeiten", label: "Abbrucharbeiten" },
    {
      value: "Abdeck-/Entsorgungsarbeiten",
      label: "Abdeck-/Entsorgungsarbeiten",
    },
    { value: "Abdeckarbeiten", label: "Abdeckarbeiten" },
    { value: "Abdeckmaterial", label: "Abdeckmaterial" },
    { value: "Absicherung", label: "Absicherung" },
    { value: "Abtragen", label: "Abtragen" },
    { value: "An-/Abfahrt", label: "An-/Abfahrt" },
    { value: "Anschlussarbeiten", label: "Anschlussarbeiten" },
    { value: "Auf-/Abbau", label: "Auf-/Abbau" },
    { value: "Aufräumarbeiten", label: "Aufräumarbeiten" },
    { value: "Ausräumarbeiten", label: "Ausräumarbeiten" },
    { value: "Baumaterialien", label: "Baumaterialien" },
    { value: "Baustelle einrichten", label: "Baustelle einrichten" },
    { value: "Be-/Entsorgung", label: "Be-/Entsorgung" },
    { value: "Befestigungsmaterial", label: "Befestigungsmaterial" },
    { value: "De-/Montage", label: "De-/Montage" },
    { value: "Demontage", label: "Demontage" },
    { value: "Entsorgung", label: "Entsorgung" },
    { value: "Fahrtkosten", label: "Fahrtkosten" },
    { value: "Fassadenreinigung", label: "Fassadenreinigung" },
    { value: "Fassadensanierung", label: "Fassadensanierung" },
    { value: "Fliesen entfernen", label: "Fliesen entfernen" },
    {
      value: "Gesamtschadensumme Stehlgut lt. Berechnung im Anhang",
      label: "Gesamtschadensumme Stehlgut lt. Berechnung im Anhang",
    },
    { value: "Gerüstung", label: "Gerüstung" },
    { value: "Isolierung", label: "Isolierung" },
    {
      value: "Klein-/Befestigungsmaterial",
      label: "Klein-/Befestigungsmaterial",
    },
    { value: "Kleinmaterial", label: "Kleinmaterial" },
    { value: "Laminat entfernen", label: "Laminat entfernen" },
    { value: "Möbelmanipulation", label: "Möbelmanipulation" },
    { value: "Montage", label: "Montage" },
    { value: "Parkett entfernen", label: "Parkett entfernen" },
    { value: "Reinigungskosten", label: "Reinigungskosten" },
    { value: "Stromverbrauch/-kosten", label: "Stromverbrauch/-kosten" },
    { value: "Tapete entfernen", label: "Tapete entfernen" },
    { value: "Vorarbeiten", label: "Vorarbeiten" },
    { value: "Vorschadenabzug", label: "Vorschadenabzug" },
    { value: "Zaunreparatur", label: "Zaunreparatur" },
    { value: "Zwischenlagerung", label: "Zwischenlagerung" },
    { value: "Zwischenmessungen", label: "Zwischenmessungen" },
  ];

  einheitOptions = [
    { value: "cm", label: "cm" },
    { value: "kg", label: "kg" },
    { value: "km", label: "km" },
    { value: "KV", label: "KV" },
    { value: "kWh", label: "kWh" },
    { value: "l", label: "l" },
    { value: "lfm", label: "lfm" },
    { value: "m", label: "m" },
    { value: "m²", label: "m²" },
    { value: "m³", label: "m³" },
    { value: "Pausch.", label: "Pausch." },
    { value: "PStd.", label: "PStd." },
    { value: "Rechn.", label: "Rechn." },
    { value: "Std.", label: "Std." },
    { value: "Stk.", label: "Stk." },
    { value: "t", label: "t" },
    { value: "Tag(e)", label: "Tag(e)" },
  ];

  gutachtenFormGroup: FormGroup<{
    [key in keyof OdataGutachten]?: FormControl;
  }> = new FormGroup({});
  positionenFormGroup: FormGroup<{
    positionenArray: FormArray<FormGroup<{ [key in keyof OdataGutachtenPosition]?: FormControl }>>;
  }>;
  formSubscriptionsGutachten: Subscription = new Subscription();
  formSubscriptionsPositionen: Subscription = new Subscription();
  selectorSubscription: Subscription;

  _isSaving: boolean = false;
  get isSaving() {
    return this._isSaving;
  }
  set isSaving(value: boolean) {
    this._isSaving = value;
    if (value) {
      this.gutachtenFormGroup.disable({ emitEvent: false });
      this.positionenFormGroup.disable({ emitEvent: false });
    } else if (!this.readonlyConditionMet) {
      this.gutachtenFormGroup.enable({ emitEvent: false });
      this.positionenFormGroup.enable({ emitEvent: false });
    }
  }

  constructor(
    private route: ActivatedRoute,
    private titelService: TitelService,
    private oidcSecurityService: OidcSecurityService,
    private loveItAlertService: LoveItAlertService,
    private gutachtenService: GutachtenService,
    private fb: FormBuilder,
    private loveItToastService: LoveItToastService,
    private changeDetectorRef: ChangeDetectorRef,
    private modalController: ModalController,
    protected authService: AuthService,
    protected platform: Platform,
  ) {
    super();
    this.dataSourceMatTable = new MatTableDataSource([]);
    this.aktId = this.route.snapshot.queryParams.aktid;
    this.gutachtenFormGroup = this.createGutachtenFormGroup(null);
    this.positionenFormGroup = this.fb.group({
      positionenArray: this.fb.array<FormGroup<{ [key in keyof OdataGutachtenPosition]?: FormControl }>>([]),
    });
  }

  @HostListener("window:beforeunload", ["$event"])
  warnOnChanges($event) {
    if (this.gutachtenFormGroup.dirty || this.positionenFormGroup.dirty) {
      $event.returnValue = "Deine Änderungen werden nicht gespeichert!";
    }
  }

  ngOnDestroy() {
    this.formSubscriptionsGutachten?.unsubscribe();
    this.selectorSubscription?.unsubscribe();
  }

  ngOnInit() {
    this.deactivatePositionen = !this.route.snapshot.data.uploadAllowed.allowed;
    if (this.deactivatePositionen) {
      this.gutachtenFormGroup.disable();
      this.positionenFormGroup.disable();
      this.positionenFormArray.controls.forEach((group) => {
        group.disable();
      });
    }

    this.titelService.titelRetrieveCreate(this.getTitelParams(this.route.snapshot)).subscribe((res) => {
      this.titel = res.Bezeichnung;
      this.shortSB2 = res.SB2;
    });
  }

  setPageDisabledState = (disabled: boolean) => {
    this.loadingGutachten = disabled;
  };

  gutachtenSelectChanged(gutachten: Gutachten) {
    this.gutachtenId = gutachten.GutachtenId;
    this.unsavedGutachtenChanges = false;
    this.unsavedPositionenChanges = false;
    this.submitted = false;
    this.positionen = [];
    this.positionenFormArray.clear();
    this.dataSourceMatTable.data = [];
    this.gutachtenFormGroup = this.createGutachtenFormGroup(null);
    this.readonlyConditionMet = gutachten.readonly;
    this.prepareKalkulation();
  }

  prepareKalkulation() {
    this.setupSort();
    this.loadingGutachten = true;
    this.gutachtenService
      .gutachtenOdataRetrieveCreate({
        akt_id: this.aktId,
        token: this.oidcSecurityService.getToken(),
        Oid: this.gutachtenId,
      })
      .subscribe((response) => {
        this.loadedGutachten = response;
        this.gutachtenFormGroup.patchValue(this.createGutachtenFormGroup(response).value);
        this.subscribeGutachtenFormGroupValueChanges();
        this.loadingGutachten = false;
      });
    this.updatePositionenList();

    if (!this.selectorSubscription) {
      this.selectorSubscription = this.selector.valueChanges.subscribe((value) => {
        this.filterDataForDatatable(value, true);
      });
    }
  }

  filterDataForDatatable(selector: KalkulationsArt | null, useFormArray: boolean = false) {
    const newData = useFormArray
      ? (
          deepCopyFormArray(this.positionenFormArray).controls as FormGroup<{
            [key in keyof OdataGutachtenPosition]?: FormControl;
          }>[]
        ).map((control) => control.value)
      : (deepCopyArray(this.positionen)?.map((entry: KalkulationGutachtenPosition) => entry.GutachtenPosition) ?? []);

    if (!selector) {
      this.dataSourceMatTable.data = newData;
    } else {
      this.dataSourceMatTable.data = newData.filter(
        (entry: OdataGutachtenPosition) => entry.KalkulationsArt === selector,
      );
    }

    this.dataSourceMatTable.data = this.dataSourceMatTable.data.sort((a, b) => a.Sort - b.Sort);
  }

  updateSortDataForDatatableWithFormGroup() {
    this.dataSourceMatTable.data.forEach((dataEntry: OdataGutachtenPosition) => {
      dataEntry.Sort = this.getPositionFormGroupByOid(dataEntry.Oid).value.Sort;
    });
    this.dataSourceMatTable.sort?.sortChange.emit(this.dataSourceMatTable.sort);
  }

  ngAfterViewInit() {
    this.setupSort();
  }

  private setupSort() {
    if (!this.sort) {
      return;
    }
    this.dataSourceMatTable.sort = this.sort;
    this.dataSourceMatTable.sort.sort({
      id: "Sort",
      start: "asc",
      disableClear: false,
    });
  }

  private getTitelParams(snapshot: ActivatedRouteSnapshot) {
    const x = {
      akt_id: snapshot.queryParams.aktid,
      snr: snapshot.queryParams.snr,
    };

    if (this.oidcSecurityService.getToken().length) {
      return { ...x, ...{ token: this.oidcSecurityService.getToken() } };
    }

    return x;
  }

  createPosition(customPosition: OdataGutachtenPosition = null, usePositionSelector: boolean = false) {
    this.createPositionen(customPosition ? [customPosition] : null, usePositionSelector);
  }

  createPositionen(customPositionen: OdataGutachtenPosition[] = null, usePositionSelector: boolean = false) {
    if (!this.selector.value && !usePositionSelector) {
      return;
    }
    this.isSaving = true;

    let positionsToCreate = [];
    if (!customPositionen?.length) {
      const newPosition = {
        Abloese: false,
        Abzug_Vorschaeden: null,
        Angebotspreis: 0,
        AngebotspreisZeit: null,
        Art: GutachtenPositionsArt.Unbekannt,
        Bemerkung: "",
        Bezeichnung: "",
        Eigenleistung: false,
        Einheit: "",
        Einheitspreis: 0,
        EinheitspreisZeit: 0,
        Gruppe: "",
        GutachtenId: this.gutachtenId,
        KalkulationsArt: this.selector.value,
        Menge: 1,
        Positionsgrupper: "",
        Pruef_OK: false,
        Sort: 0,
        UStVorhanden: true,
      } as OdataGutachtenPosition;
      const deepCopyPositionenFormArray = deepCopyFormArray(this.positionenFormArray);
      const lastSortEntry: FormGroup<{
        [key in keyof OdataGutachtenPosition]?: FormControl;
      }> = deepCopyPositionenFormArray?.controls
        ?.filter((entry) => entry.value.KalkulationsArt === this.selector.value)
        ?.sort((a, b) => b.value.Sort - a.value.Sort)[0] as FormGroup<{
        [key in keyof OdataGutachtenPosition]?: FormControl;
      }>;

      newPosition.Positionsgrupper = lastSortEntry?.value?.Positionsgrupper ?? "";
      const deepCopyGroup = this.createPositionFormGroup(newPosition);
      deepCopyPositionenFormArray.push(deepCopyGroup);
      this.recalculateSortForEntries(
        deepCopyPositionenFormArray.controls as FormGroup<{
          [key in keyof OdataGutachtenPosition]?: FormControl;
        }>[],
      );
      newPosition.Sort = deepCopyPositionenFormArray.controls.find((control) => control.value.Oid === null).value.Sort;
      positionsToCreate = [newPosition];
    } else {
      for (const customPosition of customPositionen ?? []) {
        customPosition.GutachtenId = this.gutachtenId;
        customPosition.KalkulationsArt = usePositionSelector ? customPosition.KalkulationsArt : this.selector.value;
      }
      positionsToCreate = customPositionen;
    }

    this.gutachtenService
      .gutachtenOdataPositionBatchCreateCreate({
        token: this.oidcSecurityService.getToken(),
        akt_id: this.aktId,
        GutachtenPositionen: positionsToCreate.map((position) => this.formatGutachtenPositionForSubmit(position)),
      } as OdataGutachtenPositionBatchPost)
      .subscribe({
        next: (positionen) => {
          for (const position of positionen) {
            this.positionen.push(position);
            const group = this.createPositionFormGroup(position.GutachtenPosition);
            const wasPristine = this.positionenFormArray.pristine;

            this.positionenFormArray.push(group);
            this.subscribePositionenFormGroupValueChanges();
            this.recalculateSortForPositionenFormArray(true);
            this.filterDataForDatatable(this.selector.value, true);

            if (wasPristine && this.positionenFormArray.dirty) {
              this.positionenFormArray.markAsPristine();
            }
          }
          this.isSaving = false;
          this.saveFormGroups(true);
          requestAnimationFrame(() => this.ionContent.scrollToBottom(300));
        },
        error: async (error) => {
          this.isSaving = false;
          this.updatePositionenList();
        },
      });
  }

  deletePosition(entry: OdataGutachtenPosition) {
    this.isSaving = true;
    this.gutachtenService
      .gutachtenOdataPositionDeleteCreate({
        token: this.oidcSecurityService.getToken(),
        akt_id: this.aktId,
        Oid: entry.Oid,
      } as FairAppOdataAktRequest)
      .subscribe({
        next: (response) => {
          this.positionen = this.positionen.filter((position) => position.GutachtenPosition.Oid !== entry.Oid);
          this.removePositionenFormByOid(entry.Oid);
          this.filterDataForDatatable(this.selector.value, true);
          this.recalculateSortForPositionenFormArray(true);
          this.isSaving = false;
          this.saveFormGroups(true);
        },
        error: async (error) => {
          this.isSaving = false;
          this.updatePositionenList();
        },
      });
  }

  async favouritePosition(entry: OdataGutachtenPosition) {
    this.isSaving = true;
    const position: OdataGutachtenPosition = this.getPositionFormGroupByOid(entry.Oid)?.value as OdataGutachtenPosition;
    this.gutachtenService
      .gutachtenPositionCreateCreate({
        GutachtenPosition: this.formatGutachtenPositionForSubmit(position),
        token: this.oidcSecurityService.getToken(),
        is_favourite: true,
        user_sb: (await firstValueFrom(this.oidcSecurityService.userData$))?.sb,
      })
      .subscribe({
        next: (response) => {
          this.isSaving = false;
          const existingPosition = this.positionen.find((position) => position.GutachtenPosition.Oid === entry.Oid);
          if (existingPosition) {
            Object.assign(existingPosition, response);
          }
          this.filterDataForDatatable(this.selector.value);
          this.loveItToastService.showToastMessage("Position als Favorit gespeichert.");
        },
        error: async (error) => {
          this.isSaving = false;
        },
      });
  }

  async copyPosition(entry: OdataGutachtenPosition) {
    this.isSaving = true;
    const position: OdataGutachtenPosition = this.getPositionFormGroupByOid(entry.Oid)?.value as OdataGutachtenPosition;
    this.createPosition(position);
  }

  updatePositionenList() {
    this.loadingPositionen = true;
    this.dataSourceMatTable.data = [];
    this.gutachtenService
      .gutachtenOdataPositionListCreate({
        token: this.oidcSecurityService.getToken(),
        akt_id: this.aktId,
        gutachten_id: this.gutachtenId,
      } as GutachtenRequest)
      .subscribe({
        next: (response: KalkulationGutachtenPosition[]) => {
          this.positionen = response;
          this.filterDataForDatatable(this.selector.value);
          this.loadingPositionen = false;
          this.positionenFormArray.clear();
          this.positionen.forEach((position) => {
            const group = this.createPositionFormGroup(position.GutachtenPosition);
            this.positionenFormArray.push(group);
          });
          if (this.deactivatePositionen || this.readonlyConditionMet) {
            this.positionenFormArray.controls.forEach((group) => {
              group.disable();
            });
          }
          this.subscribePositionenFormGroupValueChanges();
        },
        error: async (error) => {
          this.loadingPositionen = false;
        },
      });
  }

  async deletePositionDialogAnzeigen(entry: OdataGutachtenPosition) {
    if (this.deactivatePositionen) {
      return;
    }
    const alert = await this.loveItAlertService.create({
      header: "Position löschen?",
      message: "Die Position ist nach dem Löschen nicht mehr verfügbar.",
      buttons: [
        {
          cssClass: "color-primary",
          text: "Abbrechen",
          role: "cancel",
        },
        {
          cssClass: "color-danger",
          text: "Löschen",
          handler: () => {
            this.deletePosition(entry);
          },
        },
      ],
    });

    await alert.present();
  }

  get positionenFormArray(): FormArray {
    return this.positionenFormGroup.controls.positionenArray as FormArray;
  }

  getPositionFormGroupByOid(oid: number | string): FormGroup<{ [key in keyof OdataGutachtenPosition]?: FormControl }> {
    return this.positionenFormArray.controls.find((control) => control.value.Oid === oid) as FormGroup<{
      [key in keyof OdataGutachtenPosition]?: FormControl;
    }>;
  }

  removePositionenFormByOid(oid: number) {
    const wasPristine = this.positionenFormArray.pristine;
    for (let i = this.positionenFormArray.length - 1; i >= 0; i--) {
      if (this.positionenFormArray.at(i).value.Oid === oid) {
        this.positionenFormArray.removeAt(i);
      }
    }
    if (wasPristine && !this.positionenFormArray.dirty) {
      this.positionenFormArray.markAsPristine();
    }
  }

  createGutachtenFormGroup(gutachten: OdataGutachten): FormGroup<{ [key in keyof OdataGutachten]?: FormControl }> {
    return this.fb.group<{
      [key in keyof OdataGutachten]?: FormControl;
    }>({
      AbloeseVorschlag: new FormControl(gutachten?.AbloeseVorschlag, Validators.required),
      AblöseSumme: new FormControl(gutachten?.AblöseSumme),
      AbrechnungsArt: new FormControl(gutachten?.AbrechnungsArt, Validators.required),
      AktId: new FormControl(gutachten?.AktId),
      AutosummeAusblenden: new FormControl(gutachten?.AutosummeAusblenden),
      ErstellDatum: new FormControl(gutachten?.ErstellDatum),
      Oid: new FormControl(gutachten?.Oid),
      Poskategorie1Name: new FormControl(gutachten?.Poskategorie1Name),
      Poskategorie2Name: new FormControl(gutachten?.Poskategorie2Name),
      Poskategorie3Name: new FormControl(gutachten?.Poskategorie3Name),
      Poskategorie4Name: new FormControl(gutachten?.Poskategorie4Name),
      Poskategorie5Name: new FormControl(gutachten?.Poskategorie5Name),
      Reparaturfreigabe: new FormControl(gutachten?.Reparaturfreigabe),
      Reparaturhoehe: new FormControl(gutachten?.Reparaturhoehe),
      Status: new FormControl(gutachten?.Status),
    });
  }

  createPositionFormGroup(
    position: OdataGutachtenPosition,
  ): FormGroup<{ [key in keyof OdataGutachtenPosition]?: FormControl }> {
    this.updateDynamicNgSelectOptions(position.Oid, false);
    return this.fb.group<{
      [key in keyof OdataGutachtenPosition]?: FormControl;
    }>({
      Abloese: new FormControl(position.Abloese),
      Abzug_Vorschaeden: new FormControl(position.Abzug_Vorschaeden),
      Angebotspreis: new FormControl(position.Angebotspreis, Validators.required),
      AngebotspreisZeit: new FormControl(position.AngebotspreisZeit),
      Art: new FormControl(position.Art),
      Bemerkung: new FormControl(position.Bemerkung),
      Bezeichnung: new FormControl(position.Bezeichnung === "" ? null : position.Bezeichnung, Validators.required),
      Eigenleistung: new FormControl(position.Eigenleistung),
      Einheit: new FormControl(position.Einheit === "" ? null : position.Einheit, Validators.required),
      Einheitspreis: new FormControl(position.Einheitspreis, Validators.required),
      EinheitspreisZeit: new FormControl(position.EinheitspreisZeit),
      Gruppe: new FormControl(position.Gruppe),
      GutachtenId: new FormControl(position.GutachtenId),
      KalkulationsArt: new FormControl(position.KalkulationsArt),
      Menge: new FormControl(position.Menge),
      Oid: new FormControl(position.Oid),
      Positionsgrupper: new FormControl(position.Positionsgrupper === "" ? null : position.Positionsgrupper),
      Pruef_OK: new FormControl(position.Pruef_OK),
      Sort: new FormControl(position.Sort),
      UStVorhanden: new FormControl(position.UStVorhanden),
    });
  }

  updateDynamicNgSelectOptions(oid: number | string, useFormGroup: boolean) {
    for (const ngSelectName of Object.values(NgSelectNameEnum)) {
      this.addOptionToSelect(ngSelectName, oid, useFormGroup);
    }
  }

  addOptionToSelect(ngSelectName: NgSelectNameEnum, oid: number | string, useFormGroup: boolean) {
    if (!oid) {
      return;
    }
    let selectOptionsArray = null;
    switch (ngSelectName) {
      case NgSelectNameEnum.Positionsgrupper:
        selectOptionsArray = this.positionsgrupperOptions;
        break;
      case NgSelectNameEnum.Bezeichnung:
        selectOptionsArray = this.bezeichnugOptions;
        break;
      case NgSelectNameEnum.Einheit:
        selectOptionsArray = this.einheitOptions;
        break;
      default:
        break;
    }
    const position = useFormGroup
      ? this.getPositionFormGroupByOid(oid).value
      : this.positionen.find((position) => position.GutachtenPosition.Oid === oid).GutachtenPosition;
    if (position[ngSelectName] && selectOptionsArray) {
      (this.ngSelectValues[oid] ??= {})[ngSelectName] = position[ngSelectName];
      if (!selectOptionsArray.find((option) => option.value === position[ngSelectName])) {
        const updatedOptions = [
          ...selectOptionsArray,
          { label: position[ngSelectName], value: position[ngSelectName] },
        ].sort((a, b) => a.label.localeCompare(b.label));

        switch (ngSelectName) {
          case NgSelectNameEnum.Positionsgrupper:
            this.positionsgrupperOptions = updatedOptions;
            break;
          case NgSelectNameEnum.Bezeichnung:
            this.bezeichnugOptions = updatedOptions;
            break;
          case NgSelectNameEnum.Einheit:
            this.einheitOptions = updatedOptions;
            break;
        }
      }
    }
  }

  onSubmit() {
    this.submitted = true;
    if (this.gutachtenFormGroup.invalid || this.positionenFormGroup.invalid) {
      return;
    }
    this.recalculateSortForPositionenFormArray(true);
    this.saveFormGroups();
  }

  saveFormGroups(saveOnlySort: boolean = false): Promise<boolean> {
    this.isSaving = true;

    const dirtyPositionenFormGroups = this.positionenFormArray.controls.filter((group) => group.dirty) as FormGroup<{
      [key in keyof OdataGutachtenPosition]?: FormControl;
    }>[];

    const saveObservables: Observable<any>[] = [];

    if (dirtyPositionenFormGroups?.length > 0) {
      saveObservables.push(
        this.gutachtenService
          .gutachtenOdataPositionBatchUpdateCreate({
            akt_id: this.aktId,
            token: this.oidcSecurityService.getToken(),
            GutachtenPositionen: dirtyPositionenFormGroups.map((group) =>
              this.formatGutachtenPositionForSubmit(group.value as OdataGutachtenPosition),
            ),
            patch_only_sort: saveOnlySort,
          })
          .pipe(
            catchError((error) => {
              return of(null);
            }),
          ),
      );
    }

    if (!saveOnlySort) {
      saveObservables.push(
        this.gutachtenService
          .gutachtenOdataUpdateCreate({
            akt_id: this.aktId,
            token: this.oidcSecurityService.getToken(),
            Gutachten: this.gutachtenFormGroup.value as OdataGutachten,
          })
          .pipe(
            catchError((error) => {
              return of(null);
            }),
          ),
      );
    }
    if (saveObservables.length === 0) {
      this.isSaving = false;
      return new Promise((resolve, reject) => {
        return resolve(false);
      });
    }

    return new Promise((resolve, reject) => {
      forkJoin(saveObservables).subscribe({
        next: (responses) => {
          const allSuccessful = responses.every((res) => res !== null);
          this.isSaving = false;

          if (allSuccessful) {
            if (saveOnlySort) {
              dirtyPositionenFormGroups.forEach(
                (
                  control: FormGroup<{
                    [key in keyof OdataGutachtenPosition]?: FormControl;
                  }>,
                ) => control.controls.Sort.markAsPristine(),
              );
            } else {
              this.unsavedGutachtenChanges = false;
              this.unsavedPositionenChanges = false;
              this.gutachtenFormGroup.markAsPristine();
              dirtyPositionenFormGroups.forEach((control) => control.markAsPristine());
            }

            dirtyPositionenFormGroups.forEach((control) => {
              const position = control.value as OdataGutachtenPosition;
              this.updateDynamicNgSelectOptions((control.value as OdataGutachtenPosition).Oid, true);
              const positionUpdate = responses?.find((entry) => entry?.GutachtenPosition?.Oid === position.Oid);
              const existingPosition: KalkulationGutachtenPosition = this.positionen.find(
                (entry) => entry.GutachtenPosition.Oid === position.Oid,
              );

              if (existingPosition && !saveOnlySort) {
                if (positionUpdate) {
                  Object.assign(existingPosition, positionUpdate);
                } else if (position) {
                  Object.assign(existingPosition.GutachtenPosition, position);
                }
              } else {
                if (position) {
                  Object.assign(existingPosition.GutachtenPosition, position);
                }
              }
            });

            if (!saveOnlySort) {
              this.loadedGutachten = this.gutachtenFormGroup.value as OdataGutachten;
            }

            this.filterDataForDatatable(this.selector.value, true);
            if (saveOnlySort) {
              this.loveItToastService.showToastMessage("Sortierung aktualisiert");
            } else {
              this.loveItToastService.showToastMessage("Kalkulation gespeichert");
              this.submitted = false;
            }
            return resolve(true);
          }
          this.isSaving = false;
          return resolve(false);
        },
        error: (err) => {
          this.isSaving = false;
          return resolve(false);
        },
      });
    });
  }

  groupArrayByKey<T, K extends string | number>(array: T[], keyGetter: (item: T) => K): Record<K, T[]> {
    return array.reduce(
      (result, item) => {
        const key = keyGetter(item);
        if (!result[key]) result[key] = [];
        result[key].push(item);
        return result;
      },
      {} as Record<K, T[]>,
    );
  }

  recalculateSortForPositionenFormArray(modifySilently = false) {
    this.recalculateSortForEntries(
      this.positionenFormArray.controls as FormGroup<{
        [key in keyof OdataGutachtenPosition]?: FormControl;
      }>[],
    );
    if (!modifySilently) {
      this.unsavedPositionenChanges = true;
    }
    this.updateSortDataForDatatableWithFormGroup();
  }

  recalculateSortForEntries(
    positionenFormControls: FormGroup<{
      [key in keyof OdataGutachtenPosition]?: FormControl;
    }>[],
  ): void {
    const groupedByArt = this.groupArrayByKey(positionenFormControls, (position) => position.value.KalkulationsArt);

    const sortedArtKeys = Object.values(KalkulationsArt).filter(
      (art): art is KalkulationsArt => groupedByArt[art as KalkulationsArt] !== undefined,
    );

    let xx = 1;

    for (const artKey of sortedArtKeys) {
      const artGroup = groupedByArt[artKey];

      const runs: (typeof artGroup)[] = [];
      let currentRun: typeof artGroup = [];
      let lastGroupName: string | null = null;

      for (const entry of artGroup) {
        const groupName = entry.value.Positionsgrupper ?? "";
        if (lastGroupName === null || groupName === lastGroupName) {
          currentRun.push(entry);
        } else {
          runs.push(currentRun);
          currentRun = [entry];
        }
        lastGroupName = groupName;
      }
      if (currentRun.length) runs.push(currentRun);

      let yy = 1;
      const usedSorts = new Set<number>();

      for (const run of runs) {
        const orderedRun = [...run.filter((e) => e.value.Oid !== null), ...run.filter((e) => e.value.Oid === null)];

        let zz = 1;

        for (const entry of orderedRun) {
          const xxString = String(xx).padStart(2, "0");
          const yyString = String(yy).padStart(2, "0");

          let candidateSort: number;
          do {
            const zzString = String(zz).padStart(2, "0");
            candidateSort = +`${xxString}${yyString}${zzString}`;
            zz++;
          } while (usedSorts.has(candidateSort));

          usedSorts.add(candidateSort);

          const previousSort = entry.value.Sort;
          if (previousSort !== candidateSort) {
            entry.patchValue({ Sort: candidateSort });
            entry.controls.Sort.markAsDirty();
          }
        }

        yy++;
      }
      xx++;
    }

    this.updateSortDataForDatatableWithFormGroup();
  }

  formatSortNumberToString(sort: number): string {
    if (!Number.isFinite(sort) || sort < 0) {
      return "0.0.0";
    }

    const sortStr = Math.floor(sort).toString().padStart(4, "0");
    const zz = sortStr.slice(-2);
    const yy = sortStr.slice(-4, -2);
    const xx = sortStr.slice(0, -4) || "0";

    return `${parseInt(xx, 10)}.${parseInt(yy, 10)}.${parseInt(zz, 10)}`;
  }

  formatStringToSortNumber(input: string): number {
    if (!input || typeof input !== "string") {
      return 0;
    }

    const parts = input.split(".");
    const xx = parseInt(parts[0] || "0", 10);
    const yy = parseInt(parts[1] || "0", 10);
    const zz = parseInt(parts[2] || "0", 10);

    const sortStr = (xx > 0 ? xx.toString() : "") + yy.toString().padStart(2, "0") + zz.toString().padStart(2, "0");

    return parseInt(sortStr, 10) || 0;
  }

  subscribeGutachtenFormGroupValueChanges() {
    this.formSubscriptionsGutachten?.unsubscribe();
    this.formSubscriptionsGutachten = new Subscription();

    this.formSubscriptionsGutachten.add(
      this.gutachtenFormGroup.valueChanges.subscribe((value) => {
        this.unsavedGutachtenChanges = this.gutachtenFormGroup.dirty;
      }),
    );
  }

  subscribePositionenFormGroupValueChanges() {
    this.formSubscriptionsPositionen?.unsubscribe();
    this.formSubscriptionsPositionen = new Subscription();

    this.formSubscriptionsPositionen.add(
      this.positionenFormGroup.valueChanges.subscribe((value) => {
        this.unsavedPositionenChanges = this.positionenFormArray.dirty;
      }),
    );

    this.positionenFormGroup.controls.positionenArray.controls.forEach((positionFormGroup) => {
      const subscription = positionFormGroup.controls.Positionsgrupper.valueChanges.subscribe((value) => {
        positionFormGroup.controls.Positionsgrupper.setValue(value, {
          emitEvent: false,
        });
        this.recalculateSortForEntries(
          this.positionenFormArray.controls as FormGroup<{
            [key in keyof OdataGutachtenPosition]?: FormControl;
          }>[],
        );
      });
      this.formSubscriptionsPositionen.add(subscription);
    });
  }

  async openDiscardAlert() {
    const alert = await this.loveItAlertService.create({
      cssClass: "loeschen",
      header: "Verwerfen",
      message: "Ungespeicherte Änderungen wirklich verwerfen?",
      buttons: [
        {
          text: "Abbrechen",
          role: "cancel",
          cssClass: "color-primary",
        },
        {
          cssClass: "color-danger",
          text: "Verwerfen",
          handler: () => {
            this.discardOpenChanges();
          },
        },
      ],
    });

    await alert.present();
  }

  discardOpenChanges() {
    this.clearFormGroupChanges();
    this.gutachtenFormGroup.markAsPristine();
    this.positionenFormArray.controls.forEach((control) => control.markAsPristine());
    this.unsavedGutachtenChanges = false;
    this.unsavedPositionenChanges = false;
  }

  clearFormGroupChanges() {
    this.positionenFormArray.clear();
    this.positionen.forEach((position) => {
      const group = this.createPositionFormGroup(position.GutachtenPosition);
      this.positionenFormArray.push(group);
    });
    this.gutachtenFormGroup = this.createGutachtenFormGroup(this.loadedGutachten);
    this.subscribePositionenFormGroupValueChanges();
    this.subscribeGutachtenFormGroupValueChanges();
    this.filterDataForDatatable(this.selector.value);
  }

  onNumberInputFocus(control: FormControl) {
    if (control?.value === 0 || control?.value === "0") {
      control.setValue("");
    }
  }

  onNumberInputBlur(control: FormControl) {
    if (control?.value === null || control?.value === "") {
      control.setValue(0);
    }
  }

  onSortInputChange(event: any, oid: number | string) {
    const inputValue = event.detail.value;
    const sortNumber = this.formatStringToSortNumber(inputValue);

    const control = this.getPositionFormGroupByOid(oid).controls.Sort;
    control.markAsDirty();
    control.setValue(sortNumber, { emitEvent: true });
  }

  zeitwerteBerechnen(oid: number) {
    const formGroup: FormGroup<{
      [key in keyof OdataGutachtenPosition]?: FormControl;
    }> = this.getPositionFormGroupByOid(oid);
    const positionspreis = formGroup?.controls?.Angebotspreis?.value;
    const einheitspreis = formGroup?.controls?.Einheitspreis?.value;
    const neuwertProzent = formGroup?.controls?.Abzug_Vorschaeden?.value;
    const zeitwertAngebotspreisControl = formGroup?.controls?.AngebotspreisZeit;
    const zeitwertEinheitspreisControl = formGroup?.controls?.EinheitspreisZeit;

    if (
      positionspreis != null &&
      positionspreis !== "" &&
      neuwertProzent != null &&
      neuwertProzent !== "" &&
      !zeitwertAngebotspreisControl.dirty
    ) {
      zeitwertAngebotspreisControl.setValue(roundToDecimals(positionspreis * (neuwertProzent / 100)));
    } else {
      zeitwertAngebotspreisControl.setValue(null);
    }
    if (
      einheitspreis != null &&
      einheitspreis !== "" &&
      neuwertProzent != null &&
      neuwertProzent !== "" &&
      !zeitwertEinheitspreisControl.dirty
    ) {
      zeitwertEinheitspreisControl.setValue(roundToDecimals(einheitspreis * (neuwertProzent / 100)));
    } else {
      zeitwertEinheitspreisControl.setValue(null);
    }
  }

  positionspreisNeuwertBerechnen(oid: number) {
    const formGroup: FormGroup<{
      [key in keyof OdataGutachtenPosition]?: FormControl;
    }> = this.getPositionFormGroupByOid(oid);
    const menge = formGroup?.controls?.Menge?.value;
    const einheitspreis = formGroup?.controls?.Einheitspreis?.value;

    const positionspreisControl = formGroup?.controls?.Angebotspreis;

    if (menge != null && einheitspreis != null && !positionspreisControl.dirty) {
      positionspreisControl.setValue(roundToDecimals(menge * einheitspreis));
      this.zeitwerteBerechnen(oid);
    }
  }

  createNewTag(name: string) {
    return {
      label: name,
      value: name,
    };
  }

  formatGutachtenPositionForSubmit(position: OdataGutachtenPosition) {
    if (position.Positionsgrupper === null) {
      position.Positionsgrupper = "";
    }
    if (position.Bezeichnung === null) {
      position.Bezeichnung = "";
    }
    if (position.Einheit === null) {
      position.Einheit = "";
    }
    return position;
  }

  hasSelectorErrors(selector: KalkulationsArt): boolean {
    return this.positionenFormArray.controls
      .filter((entry) => entry.value?.KalkulationsArt === selector)
      .some((entry: FormGroup) =>
        Object.values(entry.controls).some((group: FormGroup) => group.invalid && (group.dirty || this.submitted)),
      );
  }

  replaceNumberComma(value: number): string {
    return value?.toString()?.replace(".", ",");
  }

  openKalkulationPositionskategorieModal(entry: OdataGutachtenPosition) {
    this.kalkulationsArtValueLabelOptions = this.getKalkulationsArtValueLabelOptions();
    this.currentlyEditedPosition = entry;
    this.kalkulationPositionskategorieModalKategorieFormControl = new FormControl(entry?.KalkulationsArt);
    this.kalkulationPositionskategorieModal.present();
  }

  cancelButtonKalkulationPositionskategorieModal() {
    this.kalkulationPositionskategorieModal.dismiss(null, "cancel");
  }

  saveButtonKalkulationPositionskategorieModal() {
    this.kalkulationPositionskategorieModal.dismiss(null, "confirm");
    if (this.kalkulationPositionskategorieModalKategorieFormControl?.value) {
      const formGroup = this.getPositionFormGroupByOid(this.currentlyEditedPosition?.Oid);
      formGroup?.controls?.KalkulationsArt?.setValue(this.kalkulationPositionskategorieModalKategorieFormControl.value);
      formGroup.controls?.KalkulationsArt.markAsDirty();
      this.recalculateSortForPositionenFormArray();
      this.filterDataForDatatable(this.selector.value, true);
    }
  }

  cancelButtonKalkulationsArtModal() {
    this.kalkulationsArtModal.dismiss(null, "cancel");
  }

  enableEditForSelector(selector: KalkulationsArt) {
    if (selector === this.selector.value) {
      this.kalkulationsArtModal.present();
      switch (selector) {
        case KalkulationsArt.PositionsKategorie1:
          this.currentKalkulationsArtModalFormControl = this.gutachtenFormGroup.controls.Poskategorie1Name;
          break;
        case KalkulationsArt.PositionsKategorie2:
          this.currentKalkulationsArtModalFormControl = this.gutachtenFormGroup.controls.Poskategorie2Name;
          break;
        case KalkulationsArt.PositionsKategorie3:
          this.currentKalkulationsArtModalFormControl = this.gutachtenFormGroup.controls.Poskategorie3Name;
          break;
        case KalkulationsArt.PositionsKategorie4:
          this.currentKalkulationsArtModalFormControl = this.gutachtenFormGroup.controls.Poskategorie4Name;
          break;
        case KalkulationsArt.PositionsKategorie5:
          this.currentKalkulationsArtModalFormControl = this.gutachtenFormGroup.controls.Poskategorie5Name;
          break;
        default:
          break;
      }
    }
  }

  getKalkulationsArtName(selector: KalkulationsArt): string {
    switch (selector) {
      case KalkulationsArt.Gebaeude:
        return "Gebäude";
      case KalkulationsArt.Inhalt:
        return "Inhalt";
      case KalkulationsArt.PositionsKategorie1:
        return "Positionskategorie 1";
      case KalkulationsArt.PositionsKategorie2:
        return "Positionskategorie 2";
      case KalkulationsArt.PositionsKategorie3:
        return "Positionskategorie 3";
      case KalkulationsArt.PositionsKategorie4:
        return "Positionskategorie 4";
      case KalkulationsArt.PositionsKategorie5:
        return "Positionskategorie 5";
    }
  }
  getKalkulationsArtFormControl(selector: KalkulationsArt): FormControl {
    switch (selector) {
      case KalkulationsArt.PositionsKategorie1:
        return this.gutachtenFormGroup.controls.Poskategorie1Name;
      case KalkulationsArt.PositionsKategorie2:
        return this.gutachtenFormGroup.controls.Poskategorie2Name;
      case KalkulationsArt.PositionsKategorie3:
        return this.gutachtenFormGroup.controls.Poskategorie3Name;
      case KalkulationsArt.PositionsKategorie4:
        return this.gutachtenFormGroup.controls.Poskategorie4Name;
      case KalkulationsArt.PositionsKategorie5:
        return this.gutachtenFormGroup.controls.Poskategorie5Name;
    }
  }

  getKalkulationsArtValueLabelOptions(): { value: string; label: string }[] {
    return Object.keys(KalkulationsArt).map((key) => {
      return {
        value: KalkulationsArt[key],
        label:
          this.getKalkulationsArtFormControl(KalkulationsArt[key] as KalkulationsArt)?.value ??
          this.getKalkulationsArtName(KalkulationsArt[key] as KalkulationsArt),
      };
    });
  }

  onChangeNgSelect(event, oid: number, formName: string) {
    if (event?.hasOwnProperty("label")) {
      const labelValue = event["label"];
      (this.ngSelectValues[oid] ??= {})[formName] = labelValue;

      this.updateDynamicNgSelectOptions(oid, true);
    }
  }

  onBlurNgSelect(event, oid: number, formName: string) {
    if ((this.ngSelectValues[oid] ??= {})[formName]) {
      const input = event.target;
      this.ngSelectValues[oid][formName] = input.value;
      this.updateDynamicNgSelectOptions(oid, true);
    }
  }

  getNgSelectCustomId(oid: number | string, formName: string): string {
    return `ng-select-${oid}-${formName}`;
  }

  async openPdfViewerModal(leer: boolean) {
    this.loadingPdf = true;
    this.gutachtenService
      .gutachtenOdataKalkulationPdfCreate({
        token: this.oidcSecurityService.getToken(),
        akt_id: this.aktId,
        gutachten_id: this.gutachtenId,
        leer,
      })
      .subscribe({
        next: async (pdf) => {
          const modal = await this.modalController.create({
            component: FileViewerComponent,
            cssClass: "modal-fullscreen",
            backdropDismiss: false,
            componentProps: {
              fileSource: pdf,
              fileName: `Kalkulation_${this.gutachtenId}${leer ? "_leer" : ""}_Vorschau.pdf`,
              edit: false,
            },
          });
          this.loadingPdf = false;

          await modal.present();
        },
        error: (err) => {
          this.loadingPdf = false;
        },
      });
  }

  async openPdfViewerButton(leer: boolean) {
    if (this.gutachtenFormGroup.invalid || this.positionenFormGroup.invalid) {
      this.submitted = true;
      return;
    }
    if (this.unsavedPositionenChanges || this.unsavedGutachtenChanges) {
      this.loadingPdf = true;
      const success = await this.saveFormGroups();
      if (success) {
        await this.openPdfViewerModal(leer);
      } else {
        this.loadingPdf = false;
      }
    } else {
      await this.openPdfViewerModal(leer);
    }
  }

  ensureDatatableType(position: OdataGutachtenPosition): OdataGutachtenPosition {
    return position as OdataGutachtenPosition;
  }

  async openFavoritenModal() {
    const modal = await this.modalController.create({
      component: KalkulationFavoritenListComponent,
    });

    modal.onDidDismiss().then((data) => {
      const d = data.data as OdataGutachtenPosition;
      if (d) {
        this.createPosition(d);
      }
    });

    await modal.present();
  }

  async openVorlagenModal() {
    const modal = await this.modalController.create({
      component: KalkulationVorlagenListComponent,
      componentProps: {
        currentPositionen: this.positionenFormArray?.value ?? [],
      },
    });

    modal.onDidDismiss().then((data) => {
      const d = data.data as KalkulationVorlage;
      const role = data.role;
      if (d && role === "confirm") {
        this.createPositionen(d?.kalkulation_positionen, true);
      }
    });

    await modal.present();
  }

  private getEntryWithLowestXX(sort: number, positionen: OdataGutachtenPosition[]): OdataGutachtenPosition | null {
    const sortStr = sort.toString().padStart(6, "0");
    const xx = sortStr.substring(0, 2);

    const groupEntries = positionen.filter((entry) => entry.Sort.toString().padStart(6, "0").startsWith(xx));

    if (groupEntries.length === 0) return null;

    let minXXEntry = groupEntries[0];
    let minXX = parseInt(minXXEntry.Sort.toString().padStart(6, "0").substring(0, 2), 10);

    for (const entry of groupEntries) {
      const xx = parseInt(entry.Sort.toString().padStart(6, "0").substring(0, 2), 10);
      if (xx < minXX) {
        minXX = xx;
        minXXEntry = entry;
      }
    }

    return minXXEntry;
  }

  private getEntryWithHighestXX(sort: number, positionen: OdataGutachtenPosition[]): OdataGutachtenPosition | null {
    const sortStr = sort.toString().padStart(6, "0");
    const xx = sortStr.substring(0, 2);

    const groupEntries = positionen.filter((entry) => entry.Sort.toString().padStart(6, "0").startsWith(xx));

    if (groupEntries.length === 0) return null;

    let maxXXEntry = groupEntries.reverse()[0];
    let maxXX = parseInt(maxXXEntry.Sort.toString().padStart(6, "0").substring(0, 2), 10);

    for (const entry of groupEntries) {
      const xx = parseInt(entry.Sort.toString().padStart(6, "0").substring(0, 2), 10);
      if (xx > maxXX) {
        maxXX = xx;
        maxXXEntry = entry;
      }
    }

    return maxXXEntry;
  }

  drop(event: CdkDragDrop<string>) {
    if (this.isSaving) {
      this.loveItToastService.showToastMessage("Sortierung darf nicht während des Speicherns geändert werden.");
      return;
    }
    if (this.deactivatePositionen || this.readonlyConditionMet) {
      this.loveItToastService.showToastMessage("Die Positionen können jetzt nicht sortiert werden.");
      return;
    }

    this.positionenFormGroup.controls.positionenArray.controls = this.positionenFormArray.controls.sort(
      (a, b) => a.value.Sort - b.value.Sort,
    ) as FormGroup<{ [key in keyof OdataGutachtenPosition]?: FormControl }>[];
    this.dataSourceMatTable.data = this.dataSourceMatTable.data.sort((a, b) => a.Sort - b.Sort);

    const positionFormGroup = this.getPositionFormGroupByOid(event.item.data.Oid);

    const hightestPossibleFormGroupIndex = this.positionenFormArray.controls.findIndex(
      (control) =>
        control.value.Oid ===
        this.getEntryWithHighestXX(
          positionFormGroup.value.Sort,
          this.positionenFormArray.controls.map((entry) => entry.value),
        ).Oid,
    );

    const lowestPossibleFormGroupIndex = this.positionenFormArray.controls.findIndex(
      (control) =>
        control.value.Oid ===
        this.getEntryWithLowestXX(
          positionFormGroup.value.Sort,
          this.positionenFormArray.controls.map((entry) => entry.value),
        ).Oid,
    );

    const hightestPossibleDatatableIndex = this.dataSourceMatTable.data.findIndex(
      (row) => row.Oid === this.getEntryWithHighestXX(event.item.data.Sort, this.dataSourceMatTable.data).Oid,
    );

    const lowestPossibleDatatableIndex = this.dataSourceMatTable.data.findIndex(
      (row) => row.Oid === this.getEntryWithLowestXX(event.item.data.Sort, this.dataSourceMatTable.data).Oid,
    );

    const previousIndex = this.dataSourceMatTable.data.findIndex((d) => d === event.item.data);
    const relativeIndexChange = event.currentIndex - previousIndex;
    const previousIndexFormGroup = this.positionenFormArray.controls.findIndex(
      (control) => control.value.Oid === event.item.data.Oid,
    );

    let alertNewIndexOverride = false;

    let newIndexFormGroup = previousIndexFormGroup + relativeIndexChange;

    if (newIndexFormGroup < lowestPossibleFormGroupIndex) {
      newIndexFormGroup = lowestPossibleFormGroupIndex;
      alertNewIndexOverride = true;
    }
    if (newIndexFormGroup > hightestPossibleFormGroupIndex) {
      newIndexFormGroup = hightestPossibleFormGroupIndex;
      alertNewIndexOverride = true;
    }

    let newIndexDatatable = event.currentIndex;

    if (newIndexDatatable < lowestPossibleDatatableIndex) {
      newIndexDatatable = lowestPossibleDatatableIndex;
      alertNewIndexOverride = true;
    }
    if (newIndexDatatable > hightestPossibleDatatableIndex) {
      newIndexDatatable = hightestPossibleDatatableIndex;
      alertNewIndexOverride = true;
    }

    if (alertNewIndexOverride) {
      this.loveItToastService.showToastMessage("Positionen können nicht außerhalb ihrer Kategorien sortiert werden.");
    }

    moveItemInArray(this.dataSourceMatTable.data, previousIndex, newIndexDatatable);
    moveItemInArray(this.positionenFormArray.controls, previousIndexFormGroup, newIndexFormGroup);
    this.recalculateSortForPositionenFormArray();
    this.dataSourceMatTable.data = [...this.dataSourceMatTable.data];
  }

  onXmlVorlageSelected(event: Event, input: HTMLInputElement) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const xml = reader.result as string;
        const xmlObject = xmljs.xml2js(xml, { compact: true });
        const convertedPositionen = this.convertXmlVorlageToPositionen(xmlObject);
        this.createPositionen(convertedPositionen, true);
      };
      reader.readAsText(file);
      input.value = "";
    }
  }

  convertXmlVorlageToPositionen(xml): OdataGutachtenPosition[] {
    const xmlPositionen = xml?.ArrayOfMV_Gutachten_Pos?.MV_Gutachten_Pos;
    const positionenArray = Array.isArray(xmlPositionen) ? xmlPositionen : [xmlPositionen];
    return positionenArray.map((xmlPosition) => {
      return {
        Abloese: xmlPosition?._Abloese?._text,
        Abzug_Vorschaeden: xmlPosition?._Abzug_Vorschaeden?._text,
        Angebotspreis: xmlPosition?._Preis_Angebot?._text,
        AngebotspreisZeit: xmlPosition?._Preis_Angebot_Zeit?._text,
        Art: Object.values(GutachtenPositionsArt).includes(xmlPosition?._Art?._text)
          ? xmlPosition?._Art?._text
          : GutachtenPositionsArt.Unbekannt,
        Bemerkung: xmlPosition?._Bemerkung?._text ?? "",
        Bezeichnung: xmlPosition?._Bezeichnung?._text ?? "",
        Eigenleistung: xmlPosition?._Eigenleistung?._text,
        Einheit: xmlPosition?._Einheit?._text ?? "",
        Einheitspreis: xmlPosition?._Preis_EH?._text,
        EinheitspreisZeit: xmlPosition?._Preis_EH_Zeit?._text,
        Gruppe: xmlPosition?._Gruppe?._text ?? "",
        GutachtenId: xmlPosition?._Gutachten_OID?._text,
        KalkulationsArt: Object.values(KalkulationsArt).includes(xmlPosition?._Kalkart?._text)
          ? xmlPosition?._Kalkart?._text
          : KalkulationsArt.Gebaeude,
        Menge: xmlPosition?._Menge?._text,
        Oid: xmlPosition?._OID?._text,
        Positionsgrupper: xmlPosition?._Pos_Gruppe?._text ?? "",
        Pruef_OK: xmlPosition?._Pruef_OK?._text,
        Sort: xmlPosition?._Sort?._text,
        UStVorhanden: xmlPosition?._Has_Ust?._text,
      } as OdataGutachtenPosition;
    });
  }

  sumPositionenNetto(): number {
    const isNeuwert =
      this.gutachtenFormGroup.get("AbrechnungsArt")?.value === GutachtenKalkulationAbrechnungsArt.Neuwert;
    const field = isNeuwert ? "Angebotspreis" : "AngebotspreisZeit";
    return this.positionenFormArray.value.reduce((acc, cur) => acc + Number(cur[field]), 0);
  }

  sumPositionenBrutto(): number {
    const isNeuwert =
      this.gutachtenFormGroup.get("AbrechnungsArt")?.value === GutachtenKalkulationAbrechnungsArt.Neuwert;
    const field = isNeuwert ? "Angebotspreis" : "AngebotspreisZeit";
    return this.positionenFormArray.value.reduce((acc, cur) => acc + Number(cur[field]) * 1.2, 0);
  }

  protected readonly UserRole = UserRole;
  protected readonly isMobile = isMobile;
  protected readonly Object = Object;
  protected readonly KalkulationsArt = KalkulationsArt;
  protected readonly NgSelectNameEnum = NgSelectNameEnum;
  protected readonly environment = environment;
  protected readonly odataGutachtenPositionPropertyLabels = odataGutachtenPositionPropertyLabels;
}
